import moment from 'moment';

/* Solo los campos de la persona que pinta la pantalla. La API manda un
   UserSummaryModel con más campos (username, temporaryPassword, lastLoginAt,
   createdAt, updatedAt); llegan en el objeto y no se leen, así que no rompen
   nada. */
export interface IMcpSessionUser {
  uid: string;
  names: string;
  paternalLastName: string;
  maternalLastName: string;
  email: string;
  profile: string;
}

export interface IMcpSession {
  mcpSessionId: string;
  uidUser: string;
  client: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: IMcpSessionUser;
}

export class McpSession implements IMcpSession {
  mcpSessionId: string;
  uidUser: string;
  client: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: IMcpSessionUser;

  constructor(json: IMcpSession) {
    this.mcpSessionId = json.mcpSessionId;
    this.uidUser = json.uidUser;
    this.client = json.client;
    this.lastUsedAt = json.lastUsedAt;
    this.createdAt = json.createdAt;
    this.updatedAt = json.updatedAt;
    this.user = json.user;
  }

  /* Nombre de la persona; si nunca se capturó, el correo, para que la fila no
     quede sin nadie a quien atribuirle la sesión. */
  get userFullName(): string {
    const parts: string[] = [
      this.user.names,
      this.user.paternalLastName,
      this.user.maternalLastName
    ].filter(part => !!part && part.trim() !== '');

    const fullName = parts.join(' ').trim();

    return fullName !== ''
      ? fullName
      : this.user.email;
  }

  get hasEmail(): boolean {
    return !!this.user.email && this.user.email.trim() !== '';
  }

  /* sudo y admin pueden conectarse al MCP; cualquier otro perfil se muestra
     tal cual llega. */
  get profileLabel(): string {
    switch (this.user.profile) {
      case 'sudo':
        return 'Superadministrador';
      case 'admin':
        return 'Administrador';
      default:
        return this.user.profile;
    }
  }

  get hasClient(): boolean {
    return !!this.client && this.client.trim() !== '';
  }

  get clientLabel(): string {
    return this.hasClient
      ? this.client!
      : 'Sin identificar';
  }

  get hasBeenUsed(): boolean {
    return this.lastUsedAt !== null;
  }

  get lastUsedLabel(): string {
    return this.lastUsedAt
      ? moment(this.lastUsedAt).format('DD MMM YYYY HH:mm')
      : 'Sin actividad';
  }

  get createdLabel(): string {
    return moment(this.createdAt).format('DD MMM YYYY HH:mm');
  }

  /* Lo que compara el buscador: exactamente las etiquetas que pinta la fila,
     para que buscar "sin actividad" u "oct" encuentre lo que se está leyendo. */
  get searchText(): string {
    return McpSession.normalizeSearch(
      [
        this.userFullName,
        this.hasEmail ? this.user.email : '',
        this.profileLabel,
        this.clientLabel,
        this.lastUsedLabel,
        this.createdLabel
      ].join(' ')
    );
  }

  /* Minúsculas y sin acentos: "garcia" encuentra "García". */
  static normalizeSearch(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
  }

  /* El término partido en palabras ya normalizadas. Una sesión coincide si
     contiene TODAS las palabras, en cualquier orden: "juan claude" encuentra
     a Juan Pérez conectado desde Claude Code. */
  static searchWords(term: string): string[] {
    return McpSession.normalizeSearch(term)
      .split(/\s+/)
      .filter(word => word !== '');
  }
}
